/**
 * A library template, copied into a NEW unsaved plan — the tree „Nowy program" and „Nowy plan" open
 * the builder on when the trainer starts from a template rather than from nothing.
 *
 * ── What a copy keeps, and what it must not ──────────────────────────────────────────────────
 * Everything the trainer AUTHORED travels: the scheduling mode, the phases and their lengths, every
 * day's weekday and rest, every day's and every exercise's cycle window, the sets, the progression
 * rules and the per-cycle pins. The copy is the same program; a copy that quietly reverted a window
 * to "every cycle of the phase" would put a client back in weeks the template deliberately sits out.
 *
 * Everything the SERVER owns is dropped, because the copy has never been stored anywhere:
 *
 * · the plan's `id`, so the builder's first Zapisz POSTs rather than PATCHes the template it came
 *   from — and `live`, whose concurrency token, structure snapshot and client belong to that row;
 * · each phase's `serverId`, which `PhaseGrowthPolicy` would otherwise judge against a consumed
 *   prefix that belongs to another plan;
 * · each day's `serverId` / `serverOrder` / `serverCycles`, with `liveClosedCycles` answered `[]` —
 *   a copy has been trained in no cycle, which is a statement this client can make;
 * · each exercise's `activationCycle`, `serverCycles` and `executions`, through
 *   `transplantedExercise` — the MOVE rule, which keeps the exercise's window. `cloneExercise` is the
 *   duplicate rule and drops it, which is wrong here: nothing about a template copy says "start
 *   this exercise afresh inside its day".
 *
 * ── Every day and exercise mints a FRESH slot token ──────────────────────────────────────────
 * Document-model ADR §3, and the entity's invariant. The backend only needs a token to be unique
 * within one plan, so reusing the template's would not collide on the wire — but a token is also
 * what a progression chain and an activation cycle are keyed on, and the copy is a first exposure
 * that must inherit neither.
 *
 * ── The details are the trainer's, from the create dialog ────────────────────────────────────
 * Name, description, category and cover arrive in `seed`, prefilled from the template and edited
 * there. The template's rendered cover URL survives only while the cover id is the template's own:
 * it is the server's answer for THAT id, and a different id has no URL until the plan is stored.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import { unsavedLiveContext } from './draftFactory';
import { transplantedExercise } from './draftOperations';
import { planCategoryToApi, type PlanCategoryKey } from './planCategory';
import type { DayDraft, PhaseDraft, PlanDraft } from './planDraft.types';
import { newSlotToken } from './slotToken';

/** What the trainer settled on in the create dialog's second step. */
export interface TemplateCopySeed {
  /** DATA, already trimmed by the caller. */
  readonly name: string;
  /** As typed. Empty or blank is stored as `null`, the normalisation `draftToRequest` applies too. */
  readonly description: string;
  readonly category: PlanCategoryKey;
  /** A row from the category's pool, or `null` for "let MoveBuddy choose". */
  readonly coverId: number | null;
}

function copiedDay(day: DayDraft): DayDraft {
  return {
    ...day,
    slotToken: newSlotToken(),
    serverId: null,
    serverOrder: null,
    serverCycles: null,
    liveClosedCycles: [],
    excludedCycles: [...day.excludedCycles],
    exercises: day.exercises.map(transplantedExercise),
  };
}

function copiedPhase(phase: PhaseDraft): PhaseDraft {
  return { ...phase, serverId: null, days: phase.days.map(copiedDay) };
}

/**
 * The template as a new, unsaved library entry that names the template it came from.
 *
 * @param template The template's own draft — `readPlanDocument(full).draft`, never a hand-built one.
 * @param templateId The template's server id, which the copy declares as its origin.
 */
export function copyTemplateDraft(
  template: PlanDraft,
  templateId: number,
  seed: TemplateCopySeed,
): PlanDraft {
  const phases = template.phases.map(copiedPhase);
  return {
    ...template,
    id: null,
    name: seed.name,
    description: seed.description.trim() === '' ? null : seed.description,
    category: planCategoryToApi(seed.category),
    coverId: seed.coverId,
    coverImageUrl: seed.coverId === template.coverId ? template.coverImageUrl : null,
    // A copy is the trainer's own new program, whatever the source was.
    isLibraryEntry: true,
    origin: { kind: 'copyOf', templateId },
    phases,
    live: unsavedLiveContext(phases.reduce((total, phase) => total + phase.cycles, 0)),
  };
}

/**
 * Buddy's finished draft as a new, unsaved library entry that names the conversation it came from
 * (ai-plan-builder R20, R46): accepted as a whole, with fresh identities like any copy.
 *
 * @param built The draft's own reading, `readPlanDocument(resolvedPlan(result)).draft`.
 * @param conversationId The conversation the first save binds to the new template.
 */
export function adoptAiDraft(built: PlanDraft, conversationId: number, draftKey: string | null = null): PlanDraft {
  const phases = built.phases.map(copiedPhase);
  return {
    ...built,
    id: null,
    isLibraryEntry: true,
    origin: draftKey === null ? { kind: 'aiDraft', conversationId } : { kind: 'aiDraft', conversationId, draftKey },
    phases,
    live: unsavedLiveContext(phases.reduce((total, phase) => total + phase.cycles, 0)),
  };
}
